<template>
  <div
    class="apple-price-card"
    :class="{ 'apple-price-card--external': isExternal }"
  >
    <div class="apple-price-card__text">
      <h3 class="apple-price-card__title">{{ title }}</h3>
      <p class="apple-price-card__subtitle" v-html="subtitle" />

      <Divider class="apple-price-card__divider" />

      <p class="apple-price-card__price">
        {{ plan.price.withPeriod.perMonth }}
      </p>
      <p class="apple-price-card__price-subtitle">{{ billedAtText }}</p>
    </div>

    <div class="apple-price-card__logos">
      <div
        v-for="logo in logos"
        :key="logo"
        class="apple-price-card__logo"
        :class="`apple-price-card__logo--${logo}`"
      ></div>
    </div>

    <AppButton class="apple-price-card__button" @click="emit('subscribe')">
      {{ title }}
    </AppButton>
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue'

import { PlanView } from '@/models/billing'

import Divider from '@/components/ui/Divider.vue'
import AppButton from '@/app/components/button/AppButton.vue'

const props = defineProps<{
  plan: PlanView
  isExternal?: boolean
}>()

const emit = defineEmits(['subscribe'])

const title = computed(() => {
  return props.isExternal ? 'External Payment' : 'Apple Purchase'
})

const subtitle = computed(() => {
  return props.isExternal
    ? 'External payment can be through <strong>Stripe</strong>, <strong>Apple Pay</strong>, or <strong>Google Pay</strong>.'
    : 'Payment via in-app purchase (App Store).'
})

const billedAtText = computed(() => {
  return props.plan.id === 'plan_monthly'
    ? 'Billed monthly'
    : `Billed annually at ${props.plan.price.perYear}`
})

const logos = computed(() => {
  return props.isExternal ? ['stripe', 'google', 'apple'] : ['apple']
})
</script>

<style lang="scss" scoped>
.apple-price-card {
  display: flex;
  flex-wrap: wrap;
  gap: 16px 24px;
  padding: 20px;
  border-radius: 12px;
  border: 1px solid $c-border-7;
  background: $c-white;
  box-shadow:
    0 10px 10px rgba($c-black-2, 0.04),
    0 -1px 5px rgba(0, 0, 0, 0.03);

  &__text {
    flex: 1;
  }

  &__title {
    font-size: 18px;
    line-height: 24px;
    letter-spacing: -0.02em;
  }

  &__subtitle {
    font-size: 12px;
    line-height: 16px;
    letter-spacing: -0.02em;
    color: $c-black-6;
    margin: 0;

    :deep(strong) {
      color: $c-black-3;
    }
  }

  &__divider {
    margin-block: 10px;
  }

  &__price {
    font-size: 20px;
    line-height: 24px;
    font-weight: 500;
    letter-spacing: -0.02em;
    margin-bottom: 4px;
    color: $c-black-15;
  }

  &__price-subtitle {
    font-size: 14px;
    line-height: 24px;
    letter-spacing: -0.02em;
    color: $c-black-6;
  }

  &__logos {
    display: grid;
    gap: 6px;
    height: fit-content;
    flex-shrink: 0;
  }

  &__logo {
    width: 48px;
    height: 24px;
    border-radius: 4px;
    background: $c-bg center no-repeat;
    box-shadow: 0 1px 1px rgba(0, 0, 0, 0.07);
  }

  &__logo--apple {
    background-image: url('@/assets.app/images/apple-pay.svg');
  }

  &__logo--google {
    background-image: url('@/assets.app/images/google-pay.svg');
  }

  &__logo--stripe {
    background-image: url('@/assets.app/images/stripe.svg');
  }

  &__button {
    flex-basis: 100%;
    color: $c-white;
    background: $c-orange;
    min-height: 47px;

    @include hover {
      background: $c-orange-2;
    }
  }
}

.apple-price-card--external .apple-price-card {
  &__price {
    color: $c-red;
  }
}

.night-mode .apple-price-card {
  background: $c-dark-2;
  border-color: $c-dark-5;
  box-shadow:
    0 10px 10px rgba($c-black-2, 0.2),
    0 -1px 5px rgba(0, 0, 0, 0.1);

  &__divider {
    stroke: $c-dark-5;
  }

  &__price {
    color: $c-black-5;
  }

  &__subtitle {
    :deep(strong) {
      color: $c-black-4;
    }
  }

  &__logo {
    background-color: $c-black-5;
    background-color: $c-dark-3;
  }

  &__logo--apple {
    background-image: url('@/assets.app/images/apple-pay-dark.svg');
  }

  &__logo--google {
    background-image: url('@/assets.app/images/google-pay-dark.svg');
  }

  &__logo--stripe {
    background-image: url('@/assets.app/images/stripe-dark.svg');
  }
}
</style>
